import React, { useMemo, useState } from 'react';
import {
  Activity,
  ArrowUpRight,
  BarChart3,
  CalendarCheck2,
  CalendarDays,
  Check,
  CheckCircle2,
  Circle,
  Clock3,
  DatabaseZap,
  Headphones,
  Link2,
  Megaphone,
  MessageCircle,
  Pause,
  PhoneCall,
  PhoneForwarded,
  Play,
  Radio,
  RefreshCw,
  Route,
  Search,
  Send,
  ShieldCheck,
  ThumbsDown,
  ThumbsUp,
  Webhook,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useDemoEngine } from './useDemoEngine.js';

type ViewId = 'today' | 'live' | 'journeys' | 'results' | 'connections';
type ResultTab = 'transfers' | 'appointments' | 'handoffs';

interface NavItem {
  id: ViewId;
  label: string;
  icon: LucideIcon;
}

interface DisplayLead {
  id: string;
  name: string;
  initials: string;
  phone: string;
  state: string;
  city: string;
  source: string;
  received: string;
  status: string;
  outcome: string;
  channel: string;
  preferredChannel: string;
  lastCustomerChannel: string;
  lineType: string;
  carrier: string;
}

interface DisplayEvent {
  id: string;
  atMs: number;
  time: string;
  kind: string;
  title: string;
  detail: string;
  provider: string;
  status: string;
  transcript?: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'today', label: 'Today', icon: BarChart3 },
  { id: 'live', label: 'Live activity', icon: Radio },
  { id: 'journeys', label: 'Lead journeys', icon: Route },
  { id: 'results', label: 'Results', icon: CheckCircle2 },
  { id: 'connections', label: 'Connections', icon: Link2 },
];

const FALLBACK_LEADS: DisplayLead[] = [
  {
    id: 'sample-morgan',
    name: 'Morgan Vale',
    initials: 'MV',
    phone: '(202) 555-0142',
    state: 'TX',
    city: 'Austin',
    source: 'Meta instant form',
    received: '10:42 AM',
    status: 'Conversation live',
    outcome: 'Working',
    channel: 'iMessage',
    preferredChannel: 'iMessage',
    lastCustomerChannel: 'iMessage',
    lineType: 'Mobile',
    carrier: 'Sample Wireless',
  },
  {
    id: 'sample-theo',
    name: 'Theo North',
    initials: 'TN',
    phone: '(202) 555-0118',
    state: 'FL',
    city: 'Tampa',
    source: 'ACL web funnel',
    received: '10:31 AM',
    status: 'Booked',
    outcome: 'Appointment',
    channel: 'SMS',
    preferredChannel: 'SMS',
    lastCustomerChannel: 'SMS',
    lineType: 'Mobile',
    carrier: 'Sample Mobile',
  },
  {
    id: 'sample-jamie',
    name: 'Jamie Park',
    initials: 'JP',
    phone: '(202) 555-0196',
    state: 'NC',
    city: 'Raleigh',
    source: 'Meta instant form',
    received: '10:18 AM',
    status: 'Voice only',
    outcome: 'Working',
    channel: 'Voice',
    preferredChannel: 'Voice',
    lastCustomerChannel: 'Voice',
    lineType: 'Landline',
    carrier: 'Sample Telecom',
  },
  {
    id: 'sample-casey',
    name: 'Casey Rowe',
    initials: 'CR',
    phone: '(202) 555-0175',
    state: 'OH',
    city: 'Columbus',
    source: 'Meta instant form',
    received: '9:56 AM',
    status: 'Transferred',
    outcome: 'Warm transfer',
    channel: 'Voice',
    preferredChannel: 'Voice',
    lastCustomerChannel: 'Voice',
    lineType: 'Mobile',
    carrier: 'Sample Wireless',
  },
  {
    id: 'sample-quinn',
    name: 'Quinn Bell',
    initials: 'QB',
    phone: '(202) 555-0163',
    state: 'GA',
    city: 'Savannah',
    source: 'ACL web funnel',
    received: '9:44 AM',
    status: 'Suppressed',
    outcome: 'Opted out',
    channel: 'SMS',
    preferredChannel: 'SMS',
    lastCustomerChannel: 'SMS',
    lineType: 'Mobile',
    carrier: 'Sample Mobile',
  },
];

const DEMO_NOW = new Date();

function dateAtOffset(offsetMinutes: number): Date {
  return new Date(DEMO_NOW.getTime() + offsetMinutes * 60_000);
}

function sampleClockLabel(value: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(value);
}

function dateKey(value: Date): string {
  return `${value.getFullYear()}-${value.getMonth()}-${value.getDate()}`;
}

function relativeDayLabel(value: Date): string {
  const tomorrow = new Date(DEMO_NOW);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateKey(value) === dateKey(DEMO_NOW)) return 'Today';
  if (dateKey(value) === dateKey(tomorrow)) return 'Tomorrow';
  return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(value);
}

function fullDateLabel(value: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(value);
}

const RESULT_ROWS = [
  { id: 'r1', time: '7 min ago', name: 'Casey Rowe', detail: 'Warm transfer to Jordan Lee · 4:12 connected', type: 'transfer' },
  { id: 'r2', time: '39 min ago', name: 'Riley Stone', detail: 'Warm transfer to Taylor Moss · 6:08 connected', type: 'transfer' },
  { id: 'r3', time: '1 hr ago', name: 'Avery Lake', detail: 'Warm transfer to Jordan Lee · 3:49 connected', type: 'transfer' },
  { id: 'r4', time: 'Yesterday', name: 'Parker Lane', detail: 'Warm transfer · client marked good fit', type: 'transfer' },
];

const APPOINTMENT_ROWS = [
  { id: 'a1', time: sampleClockLabel(dateAtOffset(45)), name: 'Theo North', detail: `${relativeDayLabel(dateAtOffset(45))} · Jordan Lee · Google Calendar`, type: 'booking' },
  { id: 'a2', time: sampleClockLabel(dateAtOffset(165)), name: 'Emery Coast', detail: `${relativeDayLabel(dateAtOffset(165))} · Taylor Moss · Google Calendar`, type: 'booking' },
  { id: 'a3', time: sampleClockLabel(dateAtOffset(1_440)), name: 'Reese Gray', detail: `${relativeDayLabel(dateAtOffset(1_440))} · Jordan Lee · Google Calendar`, type: 'booking' },
];

const HANDOFF_ROWS = [
  { id: 'h1', time: '18 min ago', name: 'Drew Pine', detail: '10-day sequence complete · delivered to HighLevel', type: 'handoff' },
  { id: 'h2', time: '36 min ago', name: 'Skyler West', detail: 'Disqualified · final packet delivered', type: 'handoff' },
  { id: 'h3', time: '2 hr ago', name: 'Hayden Brooks', detail: 'Engagement expired · CRM receipt confirmed', type: 'handoff' },
];

const UPCOMING_APPOINTMENTS = [
  { at: dateAtOffset(45), name: 'Theo North', detail: 'Consultation · Jordan Lee' },
  { at: dateAtOffset(165), name: 'Emery Coast', detail: 'Consultation · Taylor Moss' },
  { at: dateAtOffset(300), name: 'Robin Dale', detail: 'Callback · Jordan Lee' },
  { at: dateAtOffset(1_320), name: 'Reese Gray', detail: 'Consultation · Jordan Lee' },
  { at: dateAtOffset(1_680), name: 'Alex Birch', detail: 'Consultation · Taylor Moss' },
];

const UPCOMING_GROUPS = Array.from(
  UPCOMING_APPOINTMENTS.reduce((groups, appointment) => {
    const key = dateKey(appointment.at);
    const group = groups.get(key) ?? { date: appointment.at, appointments: [] as typeof UPCOMING_APPOINTMENTS };
    group.appointments.push(appointment);
    groups.set(key, group);
    return groups;
  }, new Map<string, { date: Date; appointments: typeof UPCOMING_APPOINTMENTS }>()),
  ([, group]) => group,
);

const CONNECTIONS = [
  {
    id: 'meta',
    name: 'Meta lead source',
    description: 'AtCostLeads campaign intake',
    detail: '2 sample forms ready · intake route connected',
    icon: Megaphone,
  },
  {
    id: 'intel',
    name: 'Phone intelligence',
    description: 'Line validation and routing',
    detail: 'Mobile, carrier, line status, and landline sample checks ready',
    icon: ShieldCheck,
  },
  {
    id: 'messages',
    name: 'Messaging',
    description: 'iMessage, RCS, and compliant SMS',
    detail: 'Apple/RCS first · carrier SMS fallback ready',
    icon: MessageCircle,
  },
  {
    id: 'voice',
    name: 'Voice agent',
    description: 'AI calling and warm transfer',
    detail: '3 sample voice lines ready · callbacks and warm transfer configured',
    icon: Headphones,
  },
  {
    id: 'calendar',
    name: 'Google Calendar',
    description: 'Jordan Lee · Main calendar',
    detail: 'Sample availability loaded · 15-minute booking buffer',
    icon: CalendarDays,
  },
  {
    id: 'crm',
    name: 'CRM delivery',
    description: 'HighLevel destination',
    detail: 'Sample delivery route ready · zero simulated failures',
    icon: DatabaseZap,
  },
];

const JOURNEY_STEPS = [
  { label: 'Received', detail: 'Meta form' },
  { label: 'Verified', detail: 'Phone intel' },
  { label: 'Routed', detail: 'Best channel' },
  { label: 'Contacted', detail: 'AI response' },
  { label: 'Qualified', detail: 'Need confirmed' },
  { label: 'Outcome', detail: 'Transfer / book' },
];

function pick<T>(value: unknown, keys: string[], fallback: T): T {
  if (!value || typeof value !== 'object') return fallback;
  const record = value as Record<string, unknown>;
  for (const key of keys) {
    const candidate = record[key];
    if (candidate !== undefined && candidate !== null && candidate !== '') return candidate as T;
  }
  return fallback;
}

function displayName(value: unknown, fallback = 'Morgan Vale'): string {
  const direct = pick<string>(value, ['name', 'fullName', 'displayName'], '');
  if (direct) return direct;
  const first = pick<string>(value, ['firstName', 'first_name'], '');
  const last = pick<string>(value, ['lastName', 'last_name'], '');
  return `${first} ${last}`.trim() || fallback;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'AL';
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return phone;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function asLead(value: unknown, fallback: DisplayLead): DisplayLead {
  const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const phoneIntel = record.phoneIntel && typeof record.phoneIntel === 'object'
    ? record.phoneIntel as Record<string, unknown>
    : {};
  const name = displayName(value, fallback.name);
  const phone = pick<string>(value, ['phone', 'formattedPhone', 'phoneNumber'], fallback.phone);
  const location = pick<string>(value, ['location'], `${fallback.city}, ${fallback.state}`);
  const locationParts = location.split(',').map((part) => part.trim()).filter(Boolean);
  const rawLineType = pick<string>(phoneIntel, ['lineType'], fallback.lineType);
  const lineType = rawLineType ? `${rawLineType[0]?.toUpperCase()}${rawLineType.slice(1)}` : fallback.lineType;
  const preferredChannel = channelLabel(pick<string>(value, ['preferredChannel'], fallback.preferredChannel));
  const rawActiveChannel = pick<string>(value, ['channelLabel', 'activeChannel', 'channel'], preferredChannel);
  const activeChannel = channelLabel(rawActiveChannel);
  const lastCustomerChannel = channelLabel(pick<string>(value, ['lastCustomerChannel'], fallback.lastCustomerChannel));
  return {
    id: pick<string>(value, ['id', 'leadId'], fallback.id),
    name,
    initials: pick<string>(value, ['initials'], initials(name)),
    phone: formatPhone(phone),
    state: pick<string>(value, ['state', 'region'], locationParts[locationParts.length - 1] ?? fallback.state),
    city: pick<string>(value, ['city'], locationParts.slice(0, -1).join(', ') || fallback.city),
    source: pick<string>(value, ['sourceLabel', 'source'], fallback.source),
    received: pick<string>(value, ['receivedLabel', 'receivedAtLabel', 'createdAtLabel', 'received'], fallback.received),
    status: pick<string>(value, ['statusLabel', 'status', 'stageLabel'], fallback.status),
    outcome: pick<string>(value, ['outcomeLabel', 'outcome'], fallback.outcome),
    channel: activeChannel,
    preferredChannel,
    lastCustomerChannel,
    lineType,
    carrier: pick<string>(phoneIntel, ['carrier'], fallback.carrier),
  };
}

function formatClock(atMs: number): string {
  const seconds = Math.max(0, Math.round(atMs / 1000));
  return `+${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function asEvent(value: unknown, index: number): DisplayEvent {
  const atMs = pick<number>(value, ['atMs', 'timestampMs', 'timeMs', 'offsetMs'], index * 12_000);
  const transcript = pick<unknown>(value, ['transcript'], null);
  return {
    id: pick<string>(value, ['id'], `event-${index}`),
    atMs,
    time: pick<string>(value, ['timeLabel', 'clockLabel'], formatClock(atMs)),
    kind: pick<string>(value, ['kind', 'type'], 'system'),
    title: pick<string>(value, ['title', 'label'], 'ACL Auto updated the lead'),
    detail: pick<string>(value, ['detail', 'description', 'body'], 'The lead journey advanced to the next approved step.'),
    provider: providerLabel(pick<string>(value, ['provider', 'providerLabel', 'actor'], 'acl_auto')),
    status: pick<string>(value, ['status', 'stage'], 'complete'),
    transcript: transcript && typeof transcript === 'object'
      ? pick<string>(transcript, ['text'], '') || undefined
      : undefined,
  };
}

function providerLabel(provider: string): string {
  const labels: Record<string, string> = {
    acl_auto: 'ACL Auto',
    meta: 'Meta',
    ipqs: 'Phone intelligence',
    sendblue: 'iMessage / RCS',
    signalhouse: 'SMS fallback',
    retell: 'Voice agent',
    elevenlabs: 'Voice agent',
    google_calendar: 'Google Calendar',
  };
  return labels[provider.toLowerCase()] ?? provider.replaceAll('_', ' ');
}

function channelLabel(channel: string): string {
  const labels: Record<string, string> = {
    meta: 'Meta',
    validation: 'Phone check',
    voice: 'Voice',
    imessage: 'iMessage',
    rcs: 'RCS',
    sms: 'SMS',
    calendar: 'Calendar',
    system: 'System',
  };
  return labels[channel.toLowerCase()] ?? channel;
}

function durationLabel(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds)) return '00:34';
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
}

function eventIcon(kind: string): LucideIcon {
  const normalized = kind.toLowerCase();
  if (normalized.includes('lead') || normalized.includes('intake')) return Webhook;
  if (normalized.includes('intel') || normalized.includes('verify')) return ShieldCheck;
  if (normalized.includes('message') || normalized.includes('sms') || normalized.includes('imessage')) return MessageCircle;
  if (normalized.includes('call') || normalized.includes('voice')) return PhoneCall;
  if (normalized.includes('transfer')) return PhoneForwarded;
  if (normalized.includes('book') || normalized.includes('calendar')) return CalendarCheck2;
  if (normalized.includes('suppress') || normalized.includes('stop')) return ShieldCheck;
  if (normalized.includes('deliver') || normalized.includes('crm')) return Send;
  return Zap;
}

function channelClass(channel: string): string {
  const normalized = channel.toLowerCase();
  if (normalized.includes('imessage') || normalized.includes('rcs')) return 'imessage';
  if (normalized.includes('sms') || normalized.includes('signal')) return 'sms';
  if (normalized.includes('voice')) return 'voice';
  if (normalized.includes('calendar')) return 'calendar';
  return 'internal';
}

function outcomeClass(outcome: string): string {
  const normalized = outcome.toLowerCase();
  if (normalized.includes('transfer')) return 'transfer';
  if (normalized.includes('book') || normalized.includes('appointment')) return 'booking';
  if (normalized.includes('suppress') || normalized.includes('opt')) return 'suppressed';
  return 'handoff';
}

function currentDateTimeLabel(): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date()).toUpperCase();
}

function SignalEqualizer() {
  return (
    <div className="equalizer" aria-hidden="true">
      <span /><span /><span /><span /><span />
    </div>
  );
}

function Brand({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={mobile ? 'mobile-brand' : 'brand-lockup'} aria-label="ACL Auto">
      <span className="brand-mark" aria-hidden="true" />
      <span className="brand-name">ACL <span>AUTO</span></span>
    </div>
  );
}

function Sidebar({ view, setView }: { view: ViewId; setView: (view: ViewId) => void }) {
  return (
    <aside className="sidebar" aria-label="Primary navigation">
      <Brand />
      <div className="workspace-stamp">
        <strong>Northstar Coverage</strong>
        <span>Sample client workspace</span>
      </div>
      <nav className="side-nav" aria-label="Client portal">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              className={`nav-button ${view === item.id ? 'active' : ''}`}
              onClick={() => setView(item.id)}
              aria-current={view === item.id ? 'page' : undefined}
            >
              <Icon size={16} strokeWidth={1.8} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="sidebar-foot">
        <div className="system-mini">
          <span className="status-dot live" />
          <span>Demo engine ready</span>
        </div>
        <p>Sample data only. No calls, messages, ads, calendar writes, or CRM events leave this workspace.</p>
      </div>
    </aside>
  );
}

function Topbar({ engine, view }: { engine: ReturnType<typeof useDemoEngine>; view: ViewId }) {
  const activeNav = NAV_ITEMS.find((item) => item.id === view) ?? NAV_ITEMS[0];
  const scenarios = engine.scenarios as unknown[];
  return (
    <header className="topbar">
      <Brand mobile />
      <div className="page-identity">
        <span className="topbar-title">{activeNav.label}</span>
        <p>Northstar Coverage · Sample account</p>
      </div>
      <div className="demo-controller">
        <span className="sample-badge"><Circle size={7} fill="currentColor" /> Sample data</span>
        <select
          className="scenario-select"
          value={engine.selectedScenarioId}
          onChange={(event) => engine.selectScenario(event.target.value as never)}
          aria-label="Demo scenario"
        >
          {scenarios.map((scenario, index) => {
            const id = pick<string>(scenario, ['id'], `scenario-${index}`);
            const label = pick<string>(scenario, ['shortTitle', 'label', 'name', 'title'], `Scenario ${index + 1}`);
            return <option key={id} value={id}>{label}</option>;
          })}
        </select>
        <button className="icon-button" type="button" onClick={engine.restart} title="Restart scenario" aria-label="Restart scenario">
          <RefreshCw size={15} />
        </button>
        <button
          className="icon-button primary"
          type="button"
          onClick={engine.togglePlayback}
          title={engine.isPlaying ? 'Pause scenario' : 'Play scenario'}
          aria-label={engine.isPlaying ? 'Pause scenario' : 'Play scenario'}
        >
          {engine.isPlaying ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" />}
        </button>
        <div className="speed-control" aria-label="Playback speed">
          {[1, 4].map((value) => (
            <button
              key={value}
              type="button"
              className={Number(engine.speed) === value ? 'active' : ''}
              onClick={() => engine.setSpeed(value as never)}
            >
              {value}x
            </button>
          ))}
        </div>
      </div>
      <div className="mobile-sample-note"><Circle size={7} fill="currentColor" /> Sample data · no real outreach</div>
    </header>
  );
}

function ScreenHeading({ title, description, meta }: { title: string; description: string; meta: string }) {
  return (
    <div className="screen-heading">
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <span className="screen-meta">{meta}</span>
    </div>
  );
}

function LiveBand({ engine, currentEvent }: { engine: ReturnType<typeof useDemoEngine>; currentEvent: DisplayEvent }) {
  const scenarioLabel = pick<string>(engine.scenario, ['label', 'name', 'title'], 'Fresh lead response');
  return (
    <section className="live-band" aria-label="ACL Auto live status">
      <div className="live-band-title">
        <span className="status-dot live" />
        <SignalEqualizer />
        <div>
          <h2>ACL Auto is working your leads</h2>
          <p>{engine.isPlaying ? 'Scenario running now' : engine.isComplete ? 'Scenario complete' : 'Playback paused'}</p>
        </div>
      </div>
      <div className="event-progress">
        <div className="event-progress-track">
          <div className="event-progress-fill" style={{ width: `${Math.max(2, Math.round(engine.progress * 100))}%` }} />
        </div>
        <div className="event-progress-labels">
          <span><strong>{engine.clockLabel}</strong></span>
          <span>{scenarioLabel}</span>
          <span>{currentEvent.provider}</span>
          <span>{Math.round(engine.progress * 100)}% complete</span>
        </div>
      </div>
      <div className="live-next">
        <span>Current action</span>
        <strong>{currentEvent.title}</strong>
      </div>
    </section>
  );
}

function MetricBand({ summary }: { summary: unknown }) {
  const totalLeads = pick<number>(summary, ['totalLeads', 'newLeads', 'leads'], 1);
  const conversations = pick<number>(summary, ['contacts', 'conversations'], 0);
  const transfers = pick<number>(summary, ['transfers'], 0);
  const bookings = pick<number>(summary, ['bookings', 'appointments'], 0);
  const firstTouchSeconds = pick<number | null>(summary, ['timeToFirstTouchSeconds'], 34);
  const metrics = [
    { label: 'New leads', value: 46 + totalLeads, sub: 'from active campaigns', trend: '+12%' },
    { label: 'Median first touch', value: durationLabel(firstTouchSeconds), sub: 'lead received to outreach', trend: 'fast' },
    { label: 'Conversations', value: 20 + conversations, sub: 'voice and messaging', trend: '+4' },
    { label: 'Warm transfers', value: 7 + transfers, sub: 'connected to your team', trend: '17%' },
    { label: 'Appointments', value: 5 + bookings, sub: 'on Google Calendar', trend: '13%' },
  ];
  return (
    <section className="metric-band" aria-label="Today metrics">
      {metrics.map((metric) => (
        <div className="metric-cell" key={metric.label}>
          <div className="metric-label">{metric.label}</div>
          <div className="metric-value"><span>{metric.value}</span><small>{metric.trend}</small></div>
          <div className="metric-sub">{metric.sub}</div>
        </div>
      ))}
    </section>
  );
}

function LeadJourneyStage({ lead, events, currentEvent, progress }: {
  lead: DisplayLead;
  events: DisplayEvent[];
  currentEvent: DisplayEvent;
  progress: number;
}) {
  const currentStep = progress >= 0.999
    ? JOURNEY_STEPS.length
    : Math.min(JOURNEY_STEPS.length - 1, Math.floor(progress * JOURNEY_STEPS.length));
  const CurrentIcon = eventIcon(currentEvent.kind);
  const customerRoute = lead.channel === 'System' ? lead.lastCustomerChannel : lead.channel;
  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>Lead in motion</h2>
          <p>Simulated from the event schema ACL Auto will use in live accounts.</p>
        </div>
        <span className={`channel-badge ${channelClass(customerRoute)}`}>{customerRoute}</span>
      </div>
      <div className="lead-stage">
        <div className="stage-main">
          <span className="lead-kicker">ACTIVE JOURNEY</span>
          <div className="lead-title-row">
            <h3>{lead.name}</h3>
            <span className="mono">{lead.city}, {lead.state} · {lead.phone}</span>
          </div>
          <div className="journey-rail">
            {JOURNEY_STEPS.map((step, index) => {
              const state = index < currentStep ? 'complete' : index === currentStep ? 'active' : '';
              return (
                <div className={`journey-step ${state}`} key={step.label}>
                  <span className="journey-node">{index < currentStep ? <Check size={12} /> : index + 1}</span>
                  <strong>{step.label}</strong>
                  <span>{step.detail}</span>
                </div>
              );
            })}
          </div>
          <div className="current-event">
            <span className="event-icon-box"><CurrentIcon size={17} /></span>
            <div>
              <strong>{currentEvent.title}</strong>
              <p>{currentEvent.detail}</p>
              {currentEvent.transcript && <blockquote>“{currentEvent.transcript}”</blockquote>}
            </div>
            <time>{currentEvent.time}</time>
          </div>
        </div>
        <aside className="stage-aside" aria-label="Phone intelligence and routing">
          <h3>Routing decision</h3>
          <div className="intel-row"><span>Line</span><strong>{lead.lineType}</strong></div>
          <div className="intel-row"><span>Carrier</span><strong>{lead.carrier}</strong></div>
          <div className="intel-row"><span>Best channel</span><strong>{lead.preferredChannel}</strong></div>
          <div className="intel-row"><span>Provider</span><strong>{currentEvent.provider}</strong></div>
          <div className="intel-row"><span>Events written</span><strong>{events.length}</strong></div>
          <div className="policy-note">Consent, local time, suppression, and attempt caps are checked before every billable action.</div>
        </aside>
      </div>
    </section>
  );
}

function OutcomesPanel() {
  const outcomes = [
    { icon: PhoneForwarded, tone: 'green', name: 'Casey Rowe', detail: 'Warm transfer connected · 4:12', time: '7m' },
    { icon: CalendarCheck2, tone: 'blue', name: 'Theo North', detail: `Booked ${relativeDayLabel(dateAtOffset(45)).toLowerCase()} at ${sampleClockLabel(dateAtOffset(45))}`, time: '39m' },
    { icon: PhoneForwarded, tone: 'green', name: 'Riley Stone', detail: 'Warm transfer connected · 6:08', time: '51m' },
    { icon: Send, tone: 'amber', name: 'Drew Pine', detail: 'Final packet delivered to CRM', time: '1h' },
  ];
  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>Outcomes today</h2>
          <p>Transfers, appointments, and completed handoffs.</p>
        </div>
        <ArrowUpRight size={15} color="var(--muted)" />
      </div>
      <ul className="outcomes-list">
        {outcomes.map((outcome) => {
          const Icon = outcome.icon;
          return (
            <li className="outcome-row" key={`${outcome.name}-${outcome.time}`}>
              <span className={`row-icon ${outcome.tone}`}><Icon size={15} /></span>
              <div><strong>{outcome.name}</strong><p>{outcome.detail}</p></div>
              <span className="row-time">{outcome.time}</span>
            </li>
          );
        })}
      </ul>
      <div className="ad-summary">
        <div className="ad-summary-head"><span>Meta spend today</span><strong className="mono">$684 / $900</strong></div>
        <div className="ad-bar"><span style={{ width: '76%' }} /></div>
        <div className="ad-summary-head" style={{ marginTop: 10 }}><span>47 leads · $14.55 CPL</span><span>Campaign on pace</span></div>
      </div>
    </section>
  );
}

function ActivityPanel({ events }: { events: DisplayEvent[] }) {
  return (
    <section className="panel">
      <div className="panel-header">
        <div><h2>Activity ledger</h2><p>Sample event history from intake through delivery.</p></div>
        <Activity size={15} color="var(--muted)" />
      </div>
      <ul className="activity-list">
        {events.slice(-6).reverse().map((event) => {
          const Icon = eventIcon(event.kind);
          return (
            <li className="activity-row" key={event.id}>
              <span className="row-icon"><Icon size={14} /></span>
              <div><strong>{event.title}</strong><p>{event.detail}</p></div>
              <span className="row-time">{event.time}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function TodayScreen({ engine, lead, events, currentEvent }: {
  engine: ReturnType<typeof useDemoEngine>;
  lead: DisplayLead;
  events: DisplayEvent[];
  currentEvent: DisplayEvent;
}) {
  return (
    <main className="screen">
      <ScreenHeading
        title="Your lead operation today"
        description="ACL Auto is receiving, checking, contacting, qualifying, and routing every new opportunity for your team."
        meta={currentDateTimeLabel()}
      />
      <LiveBand engine={engine} currentEvent={currentEvent} />
      <div className="section-gap">
        <LeadJourneyStage lead={lead} events={events} currentEvent={currentEvent} progress={engine.progress} />
      </div>
      <MetricBand summary={engine.summary} />
      <div className="today-layout">
        <ActivityPanel events={events} />
        <OutcomesPanel />
      </div>
    </main>
  );
}

function LiveActivityScreen({ engine, leads, currentEvent, setView }: {
  engine: ReturnType<typeof useDemoEngine>;
  leads: DisplayLead[];
  currentEvent: DisplayEvent;
  setView: (view: ViewId) => void;
}) {
  const sessionStories: Record<string, { status: string; copy: string; duration: string; phase: string; channel: string }> = {
    'lead-maya-sample': { status: 'Warm transfer connected', copy: 'A qualified lead reached the approved agent line with context attached.', duration: '04:12', phase: 'Complete', channel: 'Voice' },
    'lead-jordan-demo': { status: 'Appointment booked', copy: 'The lead replied after the first no-answer and selected an open Google Calendar time.', duration: '03:18', phase: 'Complete', channel: 'Calendar' },
    'lead-taylor-preview': { status: 'Voice follow-up queued', copy: 'Phone intelligence identified a landline, so messaging was skipped and the voice-only path remains active.', duration: '00:42', phase: 'Queued', channel: 'Voice' },
    'lead-casey-test': { status: 'Suppression synchronized', copy: 'A STOP reply halted voice and messaging before another action could run.', duration: '00:14', phase: 'Protected', channel: 'iMessage' },
  };
  const orderedLeads = [
    ...leads.filter((lead) => lead.id === engine.selectedLeadId),
    ...leads.filter((lead) => lead.id !== engine.selectedLeadId),
  ];
  const sessions = orderedLeads.slice(0, 4).map((lead) => {
    const backgroundStory = sessionStories[lead.id] ?? {
      status: lead.status,
      copy: 'ACL Auto is advancing the next approved action.',
      duration: '00:00',
      phase: 'Queued',
      channel: lead.lastCustomerChannel,
    };
    if (lead.id !== engine.selectedLeadId) return { ...lead, ...backgroundStory };
    return {
      ...lead,
      status: currentEvent.title,
      copy: currentEvent.detail,
      duration: engine.clockLabel,
      phase: engine.isComplete ? 'Complete' : 'Live now',
      channel: engine.isComplete ? lead.lastCustomerChannel : lead.channel,
    };
  });
  const workingCount = sessions.filter((session) => !['Complete', 'Protected'].includes(session.phase)).length;
  return (
    <main className="screen">
      <ScreenHeading
        title="Live activity"
        description="A read-only view of calls, messages, routing decisions, and the work queued behind them."
        meta={`${workingCount} WORKING · 12 QUEUED`}
      />
      <div className="live-grid">
        <section className="panel">
          <div className="panel-header"><div><h2>Working now</h2><p>Demo sessions advance as the scenario plays.</p></div><span className="phase-badge live"><span className="status-dot live" /> Live</span></div>
          <ul className="live-list">
            {sessions.map((session, index) => (
              <li className={`live-session ${index === 0 ? 'current' : ''}`} key={session.id}>
                <span className="session-avatar">{session.initials}</span>
                <div className="session-main">
                  <div className="session-head">
                    <strong>{session.name}</strong>
                    <span className={`channel-badge ${channelClass(session.channel)}`}>{session.channel}</span>
                    <span className={`phase-badge ${session.phase.toLowerCase().replace(' ', '-')}`}>{session.phase}</span>
                  </div>
                  <p><strong>{session.status}</strong> · {session.copy}</p>
                </div>
                <div className="session-meta">
                  <time>{session.duration}</time>
                  <button type="button" className="text-button" onClick={() => { engine.selectLead(session.id); setView('journeys'); }}>View journey</button>
                </div>
              </li>
            ))}
          </ul>
        </section>
        <aside aria-label="Queue and guardrails">
          <section className="panel">
            <div className="panel-header"><div><h2>Queue</h2><p>Approved work only.</p></div><Clock3 size={15} color="var(--muted)" /></div>
            <div className="queue-summary">
              <div className="queue-figure"><strong>12</strong><span>next action in <b className="mono">00:38</b></span></div>
              <div className="capacity-bars">
                <div className="capacity-item"><span>Voice lines</span><div className="capacity-track"><i style={{ width: '56%' }} /></div><strong>3 / 8</strong></div>
                <div className="capacity-item"><span>Apple / RCS</span><div className="capacity-track"><i style={{ width: '34%' }} /></div><strong>5 / 15</strong></div>
                <div className="capacity-item"><span>SMS fallback</span><div className="capacity-track"><i style={{ width: '22%' }} /></div><strong>ready</strong></div>
              </div>
            </div>
          </section>
          <section className="panel section-gap">
            <div className="panel-header"><div><h2>Guardrails</h2><p>Applied before each action.</p></div><ShieldCheck size={15} color="var(--signal)" /></div>
            <div className="panel-body">
              <div className="intel-row"><span>Recipient-local window</span><strong>Open</strong></div>
              <div className="intel-row"><span>Suppression sync</span><strong>Current</strong></div>
              <div className="intel-row"><span>Attempt caps</span><strong>Enforced</strong></div>
              <div className="intel-row"><span>Callback routing</span><strong>Ready</strong></div>
            </div>
          </section>
        </aside>
      </div>
    </main>
  );
}

function LeadJourneysScreen({ engine, leads, selectedLead, events }: {
  engine: ReturnType<typeof useDemoEngine>;
  leads: DisplayLead[];
  selectedLead: DisplayLead;
  events: DisplayEvent[];
}) {
  const [query, setQuery] = useState('');
  const filteredLeads = leads.filter((lead) => lead.name.toLowerCase().includes(query.trim().toLowerCase()));
  return (
    <main className="screen">
      <ScreenHeading
        title="Lead journeys"
        description="One continuous record of what happened, why ACL Auto chose each channel, and where the opportunity ended up."
        meta={`${leads.length} SAMPLE LEADS`}
      />
      <section className="journeys-layout">
        <aside className="journey-picker">
          <div className="journey-search"><label className="search-box"><Search size={13} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a lead" aria-label="Find a lead" /></label></div>
          <div className="journey-list">
            {filteredLeads.map((lead) => (
              <button
                type="button"
                key={lead.id}
                className={`journey-pick ${lead.id === selectedLead.id ? 'active' : ''}`}
                onClick={() => engine.selectLead(lead.id)}
              >
                <div><strong>{lead.name}</strong><p>{lead.status} · {lead.channel}</p></div>
                <time>{lead.received}</time>
              </button>
            ))}
          </div>
        </aside>
        <article className="journey-detail">
          <header className="journey-detail-head">
            <div><h2>{selectedLead.name}</h2><p>{selectedLead.phone} · {selectedLead.city}, {selectedLead.state} · {selectedLead.source}</p></div>
            <span className={`outcome-badge ${outcomeClass(selectedLead.outcome)}`}>{selectedLead.outcome}</span>
          </header>
          <ol className="timeline">
            {events.map((event) => {
              const Icon = eventIcon(event.kind);
              return (
                <li className="timeline-event" key={event.id}>
                  <time>{event.time}</time>
                  <span className="timeline-node"><Icon size={11} /></span>
                  <div className="timeline-copy"><strong>{event.title}</strong><p>{event.detail} · {event.provider}</p></div>
                </li>
              );
            })}
          </ol>
        </article>
        <aside className="journey-inspector" aria-label="Selected lead details">
          <div className="inspector-section">
            <h3>Phone intelligence</h3>
            <div className="inspector-line"><span>Line type</span><strong>{selectedLead.lineType}</strong></div>
            <div className="inspector-line"><span>Carrier</span><strong>{selectedLead.carrier}</strong></div>
            <div className="inspector-line"><span>Text eligible</span><strong>{selectedLead.lineType === 'Landline' ? 'No' : 'Yes'}</strong></div>
          </div>
          <div className="inspector-section">
            <h3>Journey</h3>
            <div className="inspector-line"><span>Current state</span><strong>{selectedLead.channel}</strong></div>
            <div className="inspector-line"><span>Last customer channel</span><strong>{selectedLead.lastCustomerChannel}</strong></div>
            <div className="inspector-line"><span>Status</span><strong>{selectedLead.status}</strong></div>
            <div className="inspector-line"><span>Recorded events</span><strong>{events.length}</strong></div>
          </div>
          <div className="inspector-section">
            <h3>Delivery</h3>
            <div className="inspector-line"><span>Destination</span><strong>HighLevel</strong></div>
            <div className="inspector-line"><span>Receipt</span><strong>Confirmed</strong></div>
            <div className="inspector-line"><span>Calendar</span><strong>Synced</strong></div>
          </div>
        </aside>
      </section>
    </main>
  );
}

function ResultsScreen() {
  const [tab, setTab] = useState<ResultTab>('transfers');
  const [feedback, setFeedback] = useState<Record<string, 'up' | 'down'>>({});
  const rows = tab === 'transfers' ? RESULT_ROWS : tab === 'appointments' ? APPOINTMENT_ROWS : HANDOFF_ROWS;
  return (
    <main className="screen">
      <ScreenHeading
        title="Results"
        description="The moments that matter: connected transfers, booked appointments, and complete handoffs into your systems."
        meta="14 OUTCOMES TODAY"
      />
      <MetricBand summary={{ totalLeads: 1, conversations: 1, transfers: 1, bookings: 1, timeToFirstTouchSeconds: 34 }} />
      <div className="results-layout section-gap">
        <section className="panel">
          <div className="panel-header">
            <div className="segment-control" aria-label="Result type">
              {([
                ['transfers', 'Transfers'],
                ['appointments', 'Appointments'],
                ['handoffs', 'Handed off'],
              ] as [ResultTab, string][]).map(([id, label]) => (
                <button key={id} type="button" className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>{label}</button>
              ))}
            </div>
            <span className="health-badge good"><Check size={11} /> Sample delivery ready</span>
          </div>
          <div className="results-list">
            {rows.map((row) => (
              <div className="result-row" key={row.id}>
                <time>{row.time}</time>
                <div><strong>{row.name}</strong><p>{row.detail}</p></div>
                <span className={`outcome-badge ${row.type}`}>{row.type === 'booking' ? 'Booked' : row.type === 'transfer' ? 'Connected' : 'Delivered'}</span>
                <div className="feedback-control" aria-label={`Rate ${row.name}`}>
                  <button type="button" className={feedback[row.id] === 'up' ? 'selected' : ''} onClick={() => setFeedback((state) => ({ ...state, [row.id]: 'up' }))} title="Good outcome"><ThumbsUp size={13} /></button>
                  <button type="button" className={feedback[row.id] === 'down' ? 'selected' : ''} onClick={() => setFeedback((state) => ({ ...state, [row.id]: 'down' }))} title="Poor outcome"><ThumbsDown size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </section>
        <aside className="panel" aria-label="Upcoming appointments">
          <div className="panel-header"><div><h2>Upcoming</h2><p>Sample calendar schedule.</p></div><CalendarDays size={15} color="var(--blue)" /></div>
          {UPCOMING_GROUPS.map((group) => (
            <div className="agenda-day" key={dateKey(group.date)}>
              <h3>{relativeDayLabel(group.date)} · {fullDateLabel(group.date)}</h3>
              {group.appointments.map((appointment) => (
                <div className="agenda-item" key={`${appointment.name}-${appointment.at.toISOString()}`}>
                  <time>{sampleClockLabel(appointment.at)}</time>
                  <div><strong>{appointment.name}</strong><span>{appointment.detail}</span></div>
                </div>
              ))}
            </div>
          ))}
        </aside>
      </div>
    </main>
  );
}

function ConnectionsScreen({ engine }: { engine: ReturnType<typeof useDemoEngine> }) {
  const transfer = engine.transfer as unknown;
  const available = pick<boolean>(transfer, ['available'], true);
  const setTransferAvailable = pick<((available: boolean) => void) | null>(engine, ['setTransferAvailable'], null);
  const [localAvailable, setLocalAvailable] = useState(available);
  const [requested, setRequested] = useState<Set<string>>(() => new Set());
  const onToggle = () => {
    const next = !localAvailable;
    setLocalAvailable(next);
    setTransferAvailable?.(next);
  };
  return (
    <main className="screen">
      <ScreenHeading
        title="Connections"
        description="A simple health view of the systems ACL Auto operates for your account. Configuration stays with the ACL team."
        meta="6 OF 6 SAMPLE READY"
      />
      <section className="transfer-setting">
        <div><h2>{localAvailable ? 'Your team is accepting live transfers' : 'Your team is booking appointments'}</h2><p>{localAvailable ? 'Qualified leads are connected to your approved transfer line.' : 'ACL Auto offers open Google Calendar times instead of transferring.'}</p></div>
        <button type="button" className={`toggle ${localAvailable ? 'on' : ''}`} onClick={onToggle} aria-pressed={localAvailable} aria-label="Accepting live transfers"><span /></button>
      </section>
      <ul className="connection-list">
        {CONNECTIONS.map((connection) => {
          const Icon = connection.icon;
          return (
            <li className="connection-row" key={connection.id}>
              <span className="connection-icon"><Icon size={19} /></span>
              <div><h2>{connection.name}</h2><p>{connection.description}</p></div>
              <div className="connection-detail">{connection.detail}</div>
              <span className="health-badge good"><span className="status-dot" /> Sample ready</span>
              <button
                type="button"
                className="request-button"
                onClick={() => setRequested((current) => new Set(current).add(connection.id))}
              >
                {requested.has(connection.id) ? 'Request sent' : 'Request change'}
              </button>
            </li>
          );
        })}
      </ul>
    </main>
  );
}

export function AclDemoApp() {
  const [view, setView] = useState<ViewId>('today');
  const engine = useDemoEngine();

  const engineLeads = engine.leads as unknown[];
  const leads = useMemo(() => {
    const mapped = engineLeads.map((lead, index) => asLead(lead, FALLBACK_LEADS[index % FALLBACK_LEADS.length]));
    return mapped.length > 0 ? mapped : FALLBACK_LEADS.slice(0, 4);
  }, [engineLeads]);

  const selectedLead = asLead(engine.lead, leads[0] ?? FALLBACK_LEADS[0]);
  const rawEvents = engine.events as unknown[];
  const events = rawEvents.length > 0
    ? rawEvents.map(asEvent)
    : [asEvent({ id: 'waiting', title: 'Lead received', detail: 'ACL Auto is preparing the first approved action.', provider: 'ACL Intake', kind: 'lead', atMs: 0 }, 0)];
  const currentEvent = events[events.length - 1];

  return (
    <div className="acl-app">
      <Sidebar view={view} setView={setView} />
      <div className="main-column">
        <Topbar engine={engine} view={view} />
        {view === 'today' && <TodayScreen engine={engine} lead={selectedLead} events={events} currentEvent={currentEvent} />}
        {view === 'live' && <LiveActivityScreen engine={engine} leads={leads} currentEvent={currentEvent} setView={setView} />}
        {view === 'journeys' && <LeadJourneysScreen engine={engine} leads={leads} selectedLead={selectedLead} events={events} />}
        {view === 'results' && <ResultsScreen />}
        {view === 'connections' && <ConnectionsScreen engine={engine} />}
      </div>
    </div>
  );
}
